import { DownOutlined } from '@ant-design/icons';
import { useState } from 'react';
import cs from 'classnames';
import Styles from '../../../Styles.module.scss';
import { getBrandDomain } from '../../../../../common/utils/getBrandDomain';

export default function Run() {
    const brandDomain = getBrandDomain();
    const [expanded, setExpanded] = useState(true);

    return (
        <div className={Styles.CommandCard}>
            <div className={Styles.CommandHeader} onClick={() => setExpanded(!expanded)}>
                <div>
                    <span className={Styles.CommandName}>modelpark run</span>
                    <span className={Styles.CommandSummary}>Start a local app and tunnel it</span>
                </div>
                <DownOutlined className={cs(Styles.CommandExpandIcon, { [Styles.expanded]: expanded })} />
            </div>

            {expanded && (
                <div className={Styles.CommandBody}>
                    <p className={Styles.CommandDescription}>
                        Start your application and create a tunnel in one step. The port is auto-detected for known
                        frameworks — just pass your start command after <code>--</code>. Runs in the background by
                        default; use <code>--session</code> to keep it in the foreground.
                    </p>

                    <div className={Styles.CommandExample}>
                        <div className={Styles.CommandExampleLabel}>Options</div>
                        <table className={Styles.OptionsTable}>
                            <thead>
                                <tr>
                                    <th>Flag</th>
                                    <th>Short</th>
                                    <th>Default</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>
                                        --name
                                        <span className={Styles.Required}>*required</span>
                                    </td>
                                    <td>-n</td>
                                    <td>-</td>
                                    <td>App name / subdomain</td>
                                </tr>
                                <tr>
                                    <td>--port</td>
                                    <td>-p</td>
                                    <td>auto-detect</td>
                                    <td>Local port (0 = auto-detect from framework)</td>
                                </tr>
                                <tr>
                                    <td>--access</td>
                                    <td>-</td>
                                    <td>private</td>
                                    <td>Access type (public, private, protected)</td>
                                </tr>
                                <tr>
                                    <td>--type</td>
                                    <td>-</td>
                                    <td>auto-detect</td>
                                    <td>App type: api, web (auto-detected from framework)</td>
                                </tr>
                                <tr>
                                    <td>--token</td>
                                    <td>-t</td>
                                    <td>-</td>
                                    <td>Authentication token (optional if logged in)</td>
                                </tr>
                                <tr>
                                    <td>--session</td>
                                    <td>-s</td>
                                    <td>false</td>
                                    <td>Run in foreground (session mode)</td>
                                </tr>
                                <tr>
                                    <td>--timeout</td>
                                    <td>-</td>
                                    <td>60s</td>
                                    <td>Max wait for port ready</td>
                                </tr>
                                <tr>
                                    <td>--server</td>
                                    <td>-</td>
                                    <td>{`wss://tunnel.${brandDomain}/ws`}</td>
                                    <td>Tunnel server WebSocket URL</td>
                                </tr>
                                <tr>
                                    <td>--api-server</td>
                                    <td>-</td>
                                    <td>{`https://${brandDomain}`}</td>
                                    <td>API server URL</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className={Styles.CommandExample}>
                        <div className={Styles.CommandExampleLabel}>Supported Frameworks (auto-detect)</div>
                        <table className={Styles.OptionsTable}>
                            <thead>
                                <tr>
                                    <th>Framework</th>
                                    <th>Default Port</th>
                                    <th>Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    ['Streamlit', '8501', 'Web'],
                                    ['Gradio', '7860', 'Web'],
                                    ['Uvicorn', '8000', 'API'],
                                    ['FastAPI', '8000', 'API'],
                                    ['Flask', '5000', 'API'],
                                    ['Gunicorn', '8000', 'API'],
                                    ['Chainlit', '8000', 'Web'],
                                    ['Panel', '5006', 'Web'],
                                    ['Voila', '8866', 'Web'],
                                    ['Jupyter', '8888', 'Web'],
                                    ['MLflow', '5000', 'Web'],
                                    ['Ollama', '11434', 'API'],
                                ].map(([name, port, type]) => (
                                    <tr key={name}>
                                        <td>{name}</td>
                                        <td>{port}</td>
                                        <td>{type}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className={Styles.CommandExample}>
                        <div className={Styles.CommandExampleLabel}>Examples</div>
                        <div className={Styles.CodeBlock} style={{ marginBottom: 8 }}>
                            <code># Streamlit — port auto-detected as 8501 (runs in background)</code>
                            <br />
                            <code>modelpark run --name myapp -- streamlit run app.py</code>
                        </div>
                        <div className={Styles.CodeBlock} style={{ marginBottom: 8 }}>
                            <code># Keep in foreground (session mode)</code>
                            <br />
                            <code>modelpark run -n myapp --session -- streamlit run app.py</code>
                        </div>
                        <div className={Styles.CodeBlock} style={{ marginBottom: 8 }}>
                            <code># Gradio with public access</code>
                            <br />
                            <code>modelpark run -n demo --access public -- python gradio_app.py</code>
                        </div>
                        <div className={Styles.CodeBlock} style={{ marginBottom: 8 }}>
                            <code># Custom port for an unknown framework</code>
                            <br />
                            <code>modelpark run -n myapp -p 9000 -- python server.py</code>
                        </div>
                        <div className={Styles.CodeBlock} style={{ marginBottom: 8 }}>
                            <code># REST API with auto-detected type</code>
                            <br />
                            <code>modelpark run --name myapi -- uvicorn main:app --port 8000</code>
                        </div>
                        <div className={Styles.CodeBlock}>
                            <code># Explicitly set API type</code>
                            <br />
                            <code>modelpark run --name myapi --type api -- python server.py</code>
                        </div>
                    </div>

                    <div className={Styles.CommandExample}>
                        <div className={Styles.CommandExampleLabel}>Output (background mode)</div>
                        <div className={Styles.CodeBlock}>
                            <code>{`Detected framework: streamlit (port 8501)
Registering app 'myapp'...
App 'myapp' registered (private)
Started 'myapp' (pid 12345, child pid 12346)
  Local:  http://localhost:8501
  Remote: https://myapp.${brandDomain}
  Logs:   ~/.modelpark/logs/myapp.log`}</code>
                        </div>
                    </div>

                    <div className={Styles.CommandExample}>
                        <div className={Styles.CommandExampleLabel}>Output (session mode with --session)</div>
                        <div className={Styles.CodeBlock}>
                            <code>{`Detected framework: streamlit (port 8501)
Registering app 'myapp'...
App 'myapp' registered (private)
Starting: streamlit run app.py
Waiting for port 8501...
Port 8501 is ready

🚀 ModelPark Run
   Local:  http://localhost:8501
   Remote: https://myapp.${brandDomain}`}</code>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
